import { CopyOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { Inventory } from '@services';
import { Button, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';

interface IProps {
  onDuplicate: (item: Inventory) => void;
}

export const Columns = ({ onDuplicate }: IProps) => [
  {
    title: L('Date added'),
    dataIndex: 'creationTime',
    key: 'creationTime',
    width: 110,
    render: (val: string) => (val ? dayjs(val).format('DD MMM YYYY') : '-'),
  },
  {
    title: L('Purchase date'),
    dataIndex: 'purchaseDate',
    key: 'purchaseDate',
    width: 110,
    render: (val: string) => (val ? dayjs(val).format('DD MMM YYYY') : '-'),
  },
  { title: L('Name'), dataIndex: 'name', key: 'name', ellipsis: true },
  { title: L('Reference'), dataIndex: 'reference', key: 'reference', width: 120, render: (val: string) => val || '-' },
  { title: L('Part #'), dataIndex: 'partNumber', key: 'partNumber', width: 120, render: (val: string) => val || '-' },
  {
    title: L('Category'),
    dataIndex: 'categoryName',
    key: 'categoryName',
    width: 120,
    render: (val: string) =>
      val ? <Tag style={{ minWidth: 90, textAlign: 'center' }}>{val}</Tag> : '-',
  },
  { title: L('Supplier'), dataIndex: 'supplierName', key: 'supplierName', width: 140, ellipsis: true },
  { title: L('Invoice #'), dataIndex: 'supplierInvoiceNumber', key: 'invoiceNum', width: 120 },
  {
    title: L('Qty'),
    dataIndex: 'quantity',
    key: 'quantity',
    width: 90,
    render: (val: number, record: Inventory) =>
      record.isLowStock ? (
        <Tooltip title={L('Stock at or below minimum')}>
          <Tag color="red" style={{ margin: 0 }}>{val}</Tag>
        </Tooltip>
      ) : (
        <span>{val}</span>
      ),
  },
  {
    title: L('Cost (£)'),
    dataIndex: 'costPrice',
    key: 'costPrice',
    width: 90,
    render: (val: number) => val?.toFixed(2),
    exportRender: (val: number) => val?.toFixed(2),
  },
  {
    title: L('Sale (£)'),
    dataIndex: 'sellPrice',
    key: 'sellPrice',
    width: 90,
    render: (val: number) => val?.toFixed(2),
    exportRender: (val: number) => val?.toFixed(2),
  },
  {
    title: L('Margin'),
    dataIndex: 'margin',
    key: 'margin',
    width: 80,
    render: (val: number) => {
      if (!val && val !== 0) return '-';
      const color = val > 0 ? '#52c41a' : '#ff4d4f';
      return <span style={{ color, fontWeight: 500 }}>{val.toFixed(1)}%</span>;
    },
    exportRender: (val: number) => (val ? `${val.toFixed(1)}%` : '-'),
  },
  {
    title: L('Regular item'),
    dataIndex: 'isInShop',
    key: 'isInShop',
    width: 130,
    render: (val: boolean, record: Inventory) =>
      val ? (
        <Tooltip title={record.location || L('No location set')}>
          <Tag color="green" style={{ margin: 0 }}>
            ✓ {record.location ? record.location : L('Regular item')}
          </Tag>
        </Tooltip>
      ) : (
        <Tag style={{ margin: 0, color: '#8c8c8c' }}>-</Tag>
      ),
    exportRender: (val: boolean, record: Inventory) =>
      val ? (record.location ? `In shop (${record.location})` : 'Regular item') : 'No',
  },
  {
    title: L('Action'),
    key: 'action',
    width: 120,
    render: (record: Inventory) => (
      <Button
        size="small"
        icon={<CopyOutlined />}
        onClick={(e) => {
          e.stopPropagation();
          onDuplicate(record);
        }}
      >
        {L('Duplicate')}
      </Button>
    ),
  },
];

const formatDateUk = (val: string) => (val ? dayjs(val).format('DD/MM/YYYY') : '');

export const ExportColumns = [
  { title: L('Purchase Date'), dataIndex: 'purchaseDate', exportRender: formatDateUk },
  { title: L('Date added'), dataIndex: 'creationTime', exportRender: formatDateUk },
  { title: L('Supplier'), dataIndex: 'supplierName' },
  { title: L('Supplier Inv #'), dataIndex: 'supplierInvoiceNumber' },
  { title: L('Supplier Part #'), dataIndex: 'partNumber' },
  { title: L('Garage Ref #'), dataIndex: 'reference' },
  { title: L('Part Name'), dataIndex: 'name' },
  { title: L('Category'), dataIndex: 'categoryName' },
  { title: L('Qty'), dataIndex: 'quantity' },
  { title: L('Regular item'), dataIndex: 'isInShop', exportRender: (val: boolean) => (val ? 'Yes' : 'No') },
  { title: L('Min Stock'), dataIndex: 'minStockLevel' },
  { title: L('Puchase £'), dataIndex: 'costPrice', exportRender: (val: number) => (val ?? 0).toFixed(2) },
  { title: L('Retail £'), dataIndex: 'sellPrice', exportRender: (val: number) => (val ?? 0).toFixed(2) },
  { title: L('Margin %'), dataIndex: 'margin', exportRender: (val: number) => (val != null ? `${val.toFixed(1)}%` : '') },
  { title: L('Part Location'), dataIndex: 'location' },
  { title: L('Umoto ID'), dataIndex: 'id' },
];
